import { LoadingSkeletonList } from '@/components/SkeletonLoading';
import {
  useGetTransactionItemDetail,
  useGetTransactionItemsByReportType,
} from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { useTransactionItemDetailEdit } from '@/features/transaction_items/Detail/context/TransactionItemDetailEdit';
import { countVisibleCustomTransactionItems } from '@/features/transaction_items/utils/countVisibleCustomTransactionItems';
import { useTranslation } from '@/i18n';
import { ButtonV2 } from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import {
  Dispatch,
  FC,
  SetStateAction,
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import { DetailTopView } from './DetailTopView';
import { EditForm } from './EditForm';
import styles from './Top.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  isLoading?: boolean;
  label: string;
  visibility: boolean;
  is_required: boolean;
  tooltip_message: string;
  isSupportedOffice: boolean;
  isFlexibleValidationEnabled: boolean;
  setDetailTopLoading: Dispatch<SetStateAction<boolean>>;
};

export type DetailTopInnerProps = {
  isLoading?: boolean;
  isSupportedOffice: boolean;
  isActiveCustomItemCountLimitEnabled: boolean;
  isFlexibleValidationEnabled: boolean;
  setDetailTopLoading: Dispatch<SetStateAction<boolean>>;
  setTransactionItemData?: (
    data: {
      can_delete?: boolean;
      delete_tooltip?: string;
    } | null
  ) => void;
};

export const DetailTop: FC<Props> = memo(
  ({
    isLoading = false,
    label,
    visibility,
    is_required,
    tooltip_message,
    isSupportedOffice,
    setDetailTopLoading,
    isFlexibleValidationEnabled,
  }) => {
    const { t } = useTranslation();
    const { onEdit } = useTransactionItemDetailEdit();

    useEffect(() => {
      setDetailTopLoading(isLoading);
    }, [isLoading, setDetailTopLoading]);

    const handleEdit = useCallback(() => {
      onEdit();
    }, [onEdit]);

    return (
      <div className={cx(styles['container'])}>
        <div className={cx(styles['button-container'])}>
          {isSupportedOffice && (
            <ButtonV2 isSecondary isDisabled={isLoading} onClick={handleEdit}>
              {t('edit')}
            </ButtonV2>
          )}
        </div>
        {isLoading ? (
          <LoadingSkeletonList repeatCount={4} />
        ) : (
          <DetailTopView
            label={label}
            visibility={visibility}
            is_required={is_required}
            tooltip_message={tooltip_message}
            isFlexibleValidationEnabled={isFlexibleValidationEnabled}
          />
        )}
      </div>
    );
  }
);
DetailTop.displayName = 'DetailTop';

export const DetailTopInner: FC<DetailTopInnerProps> = memo(
  ({
    isLoading = false,
    setDetailTopLoading,
    setTransactionItemData,
    isSupportedOffice,
    isActiveCustomItemCountLimitEnabled,
    isFlexibleValidationEnabled,
  }) => {
    const { transaction_item_id, report_type_id } = useParams();
    const { isEdit } = useTransactionItemDetailEdit();
    const { data } = useGetTransactionItemDetail(transaction_item_id ?? '');
    const shouldApplyVisibleCustomLimit =
      isActiveCustomItemCountLimitEnabled && data?.type === 'custom';

    const { data: transactionItems } = useGetTransactionItemsByReportType(
      report_type_id ?? '',
      Boolean(shouldApplyVisibleCustomLimit && report_type_id)
    );

    const visibleCustomItemsInUseCount = useMemo(() => {
      if (!shouldApplyVisibleCustomLimit) return undefined;
      if (transactionItems === null || transactionItems === undefined)
        return undefined;
      return countVisibleCustomTransactionItems(transactionItems);
    }, [shouldApplyVisibleCustomLimit, transactionItems]);

    useEffect(() => {
      if (setTransactionItemData) {
        setTransactionItemData(
          data
            ? {
                can_delete: data.can_delete,
                delete_tooltip: data.delete_tooltip ?? undefined,
              }
            : null
        );
      }
    }, [data, setTransactionItemData]);

    if (isEdit) {
      return (
        <EditForm
          label={data?.label ?? ''}
          visibility={data?.is_visible ?? false}
          is_required={
            isFlexibleValidationEnabled ? data?.is_required ?? false : undefined
          }
          tooltip_message={data?.tooltip_message ?? ''}
          visibleCustomItemsInUseCount={visibleCustomItemsInUseCount}
          isFlexibleValidationEnabled={isFlexibleValidationEnabled}
          isActiveCustomItemCountLimitEnabled={shouldApplyVisibleCustomLimit}
        />
      );
    }

    return (
      <DetailTop
        isLoading={isLoading}
        label={data?.label ?? ''}
        visibility={data?.is_visible ?? false}
        is_required={data?.is_required ?? false}
        tooltip_message={data?.tooltip_message ?? ''}
        isSupportedOffice={isSupportedOffice}
        isFlexibleValidationEnabled={isFlexibleValidationEnabled}
        setDetailTopLoading={setDetailTopLoading}
      />
    );
  }
);
DetailTopInner.displayName = 'DetailTopInner';

export const DetailTopContainer: FC<{
  isSupportedOffice: boolean;
  isActiveCustomItemCountLimitEnabled: boolean;
  isFlexibleValidationEnabled: boolean;

  setDetailTopLoading: Dispatch<SetStateAction<boolean>>;
  setTransactionItemData?: (
    data: {
      can_delete?: boolean;
      delete_tooltip?: string;
    } | null
  ) => void;
}> = memo(
  ({
    isSupportedOffice,
    isActiveCustomItemCountLimitEnabled,
    isFlexibleValidationEnabled,
    setDetailTopLoading,
    setTransactionItemData,
  }) => {
    return (
      <Suspense
        fallback={
          <DetailTop
            isLoading
            label=''
            visibility={false}
            is_required={false}
            tooltip_message=''
            isSupportedOffice={isSupportedOffice}
            isFlexibleValidationEnabled={isFlexibleValidationEnabled}
            setDetailTopLoading={setDetailTopLoading}
          />
        }
      >
        <DetailTopInner
          isSupportedOffice={isSupportedOffice}
          isActiveCustomItemCountLimitEnabled={
            isActiveCustomItemCountLimitEnabled
          }
          isFlexibleValidationEnabled={isFlexibleValidationEnabled}
          setDetailTopLoading={setDetailTopLoading}
          setTransactionItemData={setTransactionItemData}
        />
      </Suspense>
    );
  }
);
DetailTopContainer.displayName = 'DetailTopContainer';
